import React, { useEffect } from 'react'
import { currentLanguageCodeVar, currentMarketIdVar } from 'graphql/reactive'
import { useTranslation } from 'lib/i18n'
import dynamic from 'next/dynamic'
import { Configure, connectStateResults, Index } from 'react-instantsearch-dom'
import { useDebouncedCallback } from 'use-debounce'

import CtaLink from 'components/CtaLink/CtaLink'
import ErrorMessage from 'components/ErrorMessage/ErrorMessage'
import Heading from 'components/Heading/Heading'
import Link from 'components/Link/Link'
import NoResult from 'components/SearchResults/NoResult'
import Loading from 'components/UI/Loading/Loading'
import useAlgoliaIndices from 'hooks/useAlgoliaIndices'
import useFeatureFlags from 'hooks/useFeatureFlags'

import { searchCompleted } from '../../lib/tracking/avo/sdk'
import InfiniteSearchResultList from './InfiniteSearchResultList'

import styles from './SearchResults.module.css'

const ClearSearchButton = dynamic(() => import('./ClearSearchButton'))

const POTENTIAL_PROFILES_DEFAULT_FILTERS = ''

const SearchResults = ({
  searchTerm,
  searching,
  isSearchStalled,
  allSearchResults,
  error,
}) => {
  const { t } = useTranslation()
  const { profilesIndex, potentialProfilesIndex } = useAlgoliaIndices()
  const { searchFilter } = useFeatureFlags()

  const profileResults = allSearchResults && allSearchResults[profilesIndex]
  const potentialProfilesResult =
    allSearchResults && allSearchResults[potentialProfilesIndex]

  const noHits =
    profileResults?.nbHits === 0 && potentialProfilesResult?.nbHits === 0

  const marketId = currentMarketIdVar()
  const languageId = currentLanguageCodeVar()

  const [debouncedSearchCompleted] = useDebouncedCallback(searchCompleted, 500)

  useEffect(() => {
    const potentialProfilesSearchResults = potentialProfilesResult?.nbHits || 0
    const profileSearchResults = profileResults?.nbHits || 0
    const numberOfSearchResults =
      potentialProfilesSearchResults + profileSearchResults
    debouncedSearchCompleted({
      marketId,
      localeId: languageId,
      numberOfSearchResults,
      profileSearchResults,
      potentialProfilesSearchResults,
      keywords: searchTerm,
    })
  }, [profileResults, potentialProfilesResult, searchTerm])

  return (
    <>
      <Loading show={isSearchStalled} />

      {!searching && error && (
        <ErrorMessage message="Error loading search results." />
      )}

      {!searching && !error && noHits && (
        <NoResult searchTerm={searchTerm} filterEnabled={searchFilter} />
      )}

      <div className={styles.resultsWrapper}>
        <InfiniteSearchResultList />
        {potentialProfilesResult?.nbHits !== 0 && !searching && (
          <div className={styles.searchResultSuggest}>
            <Heading level="h3">
              <h2>
                {t(
                  'SearchResults.potentialProfilesHeading',
                  'Not what you were looking for?',
                )}
              </h2>
            </Heading>
            <p>
              {t(
                'SearchResults.potentialProfilesText',
                'Help us get these profiles on memmo.',
              )}
            </p>
          </div>
        )}
        <Index indexName={potentialProfilesIndex}>
          <Configure filters={POTENTIAL_PROFILES_DEFAULT_FILTERS} />
          <InfiniteSearchResultList potentialProfiles />
        </Index>
        {potentialProfilesResult?.nbHits !== 0 && !searching && (
          <>
            <p className={styles.resultsSuggestLink}>
              {t(
                'searchResults.suggestText',
                'Still no match? Try a broader search term or',
              )}{' '}
              <Link href={`/suggest?searchTerm=${searchTerm}`} passHref>
                <CtaLink>
                  {t('searchResults.goToSuggestion', 'suggest a profile')}
                </CtaLink>
              </Link>
            </p>

            {searchFilter && (
              <div className={styles.clearFilterButton}>
                <ClearSearchButton clearsQuery />
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}

export default connectStateResults(SearchResults)
